import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import Logo from "./Logo";
import { scrollToId } from "./scroll";
import { openBillingPortal, startGoogleSignIn } from "./auth";

export default function Navbar({ user, authChecked, onLogout }) {
  const [portalLoading, setPortalLoading] = useState(false);

  const openPortal = async () => {
    if (portalLoading) return;
    setPortalLoading(true);
    try {
      await openBillingPortal();
    } catch {
      // portal unavailable (no subscription yet)
    } finally {
      setPortalLoading(false);
    }
  };
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -64, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled
          ? "border-b border-white/5 bg-[#05060F]/80 backdrop-blur-xl"
          : "border-b border-transparent bg-transparent"
      }`}
      data-testid="navbar"
    >
      <nav className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-6 lg:px-10">
        <button
          onClick={() => scrollToId("#top")}
          className="flex items-center gap-2.5"
          data-testid="nav-logo"
        >
          <Logo className="h-9 w-9" />
          <span className="font-display text-lg font-semibold tracking-tight text-white">
            StartX <span className="text-teal-300">Powered</span>
          </span>
        </button>

        <div className="hidden items-center gap-9 md:flex">
          <button
            onClick={() => scrollToId("#features")}
            className="text-sm font-medium text-slate-400 transition-colors duration-300 hover:text-white"
            data-testid="nav-features-link"
          >
            Features
          </button>
          <button
            onClick={() => scrollToId("#pricing")}
            className="text-sm font-medium text-slate-400 transition-colors duration-300 hover:text-white"
            data-testid="nav-pricing-link"
          >
            Pricing
          </button>
        </div>

        <div className="flex items-center gap-3">
          {authChecked && user ? (
            <div className="flex items-center gap-3" data-testid="nav-user-menu">
              {user.plan_status === "active" && user.plan && (
                <span
                  className="hidden rounded-full border border-teal-400/30 bg-teal-400/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-teal-300 md:inline-block"
                  data-testid="nav-plan-badge"
                >
                  {user.plan}
                </span>
              )}
              {user.picture ? (
                <img
                  src={user.picture}
                  alt={user.name}
                  className="h-9 w-9 rounded-full border border-teal-400/40"
                  data-testid="nav-user-avatar"
                />
              ) : (
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-blue-600 to-teal-500 text-sm font-semibold text-white">
                  {(user.name || user.email || "U")[0].toUpperCase()}
                </span>
              )}
              {user.stripe_customer_id && (
                <button
                  onClick={openPortal}
                  disabled={portalLoading}
                  className="hidden text-sm font-medium text-slate-400 transition-colors duration-300 hover:text-white disabled:opacity-50 md:block"
                  data-testid="nav-billing-button"
                >
                  {portalLoading ? "Opening…" : "Billing"}
                </button>
              )}
              <button
                onClick={onLogout}
                className="text-sm font-medium text-slate-400 transition-colors duration-300 hover:text-white"
                data-testid="nav-logout-button"
              >
                Sign out
              </button>
            </div>
          ) : (
            <button
              onClick={startGoogleSignIn}
              className="rounded-full border border-white/15 bg-white/5 px-5 py-2.5 text-sm font-semibold text-white transition-[background-color,transform] duration-300 hover:scale-[1.03] hover:bg-white/10"
              data-testid="nav-signin-button"
            >
              Sign in
            </button>
          )}
          <button
            onClick={() => scrollToId("#pricing")}
            className="group flex items-center gap-2 rounded-full bg-gradient-to-r from-blue-600 via-cyan-600 to-teal-600 px-5 py-2.5 text-sm font-semibold text-white shadow-[0_0_28px_rgba(34,211,238,0.35)] transition-[transform,box-shadow] duration-300 hover:scale-[1.03] hover:shadow-[0_0_40px_rgba(34,211,238,0.55)]"
            data-testid="nav-cta-button"
          >
            Start Free Trial
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
          </button>
        </div>
      </nav>
    </motion.header>
  );
}
